/**
 * MSAL token cache persistence
 *
 * Uses @azure/msal-node-extensions for secure token storage:
 * - Windows: DPAPI (Data Protection API)
 * - macOS: Keychain
 * - Linux: libsecret if available, native fs fallback otherwise
 *
 * The native fs fallback handles KDE (KWallet), headless servers, and other
 * non-GNOME environments where msal-node-extensions fails.
 */

import { join } from "node:path";
import { env, platform } from "node:process";
import { readFileSync, writeFileSync, existsSync, chmodSync, unlinkSync } from "node:fs";
import { TokenCacheContext, type ICachePlugin } from "@azure/msal-node";
import { getConfigDir } from "../config/loader.js";
import { logger } from "../utils/logger.js";
import { TOKEN_CACHE_FILE_NAME } from "../utils/constants.js";

/**
 * How long a secure-storage operation (keychain/DPAPI read, lock acquisition)
 * may take before we fail loudly instead of hanging the MCP transport forever.
 * Observed live: macOS keytar.getPassword blocks indefinitely when Security
 * wants an authorization prompt that a non-UI context (an MCP host spawning
 * the server headless) cannot render.
 */
function getSecureStorageTimeoutMs(): number {
  return Number.parseInt(env["PA_MCP_SECURE_STORAGE_TIMEOUT_MS"] ?? "", 10) || 15_000;
}

function secureStorageTimeoutError(operation: string, timeoutMs: number): Error {
  return new Error(
    `Secure token storage did not respond within ${timeoutMs / 1000}s (${operation}). ` +
    "The operating system may be waiting for a keychain authorization prompt this process cannot display. " +
    "Run `powerautomate-mcp --doctor` once from a normal terminal (approve any keychain prompt with " +
    "\"Always Allow\"), then restart your AI app."
  );
}

interface AbortableCachePlugin extends ICachePlugin {
  abortCacheAccess(context: TokenCacheContext): Promise<void>;
}

/** Preserve transaction identity while refusing a save. Native-file plugins need no lock cleanup. */
async function abortCacheAccess(plugin: ICachePlugin, context: TokenCacheContext): Promise<void> {
  const abortable = plugin as Partial<AbortableCachePlugin>;
  if (abortable.abortCacheAccess) return abortable.abortCacheAccess(context);
  return plugin.afterCacheAccess(new TokenCacheContext(context.tokenCache, false));
}

/**
 * One public SDK persistence plugin per transaction: its private lock handle
 * must never be reused after unlock. Force write-intent BEFORE even for reads
 * so the SDK holds any acquired lock until our one paired AFTER. No SDK internals
 * are inspected. Aborted pending work cleans up without saving when it settles;
 * a native promise that never settles cannot be canceled through this API.
 */
export class TransactionalCachePlugin implements AbortableCachePlugin {
  private readonly transactions = new WeakMap<TokenCacheContext, {
    plugin: ICachePlugin;
    settled: boolean;
    aborted: boolean;
    completion?: Promise<void>;
  }>();

  constructor(private readonly createPlugin: () => ICachePlugin) {}

  async beforeCacheAccess(context: TokenCacheContext): Promise<void> {
    if (this.transactions.has(context)) throw new Error("Token cache transaction is already active.");
    const transaction = { plugin: this.createPlugin(), settled: false, aborted: false };
    this.transactions.set(context, transaction);
    try {
      await transaction.plugin.beforeCacheAccess(new TokenCacheContext(context.tokenCache, true));
    } catch (error) {
      transaction.settled = true;
      try {
        await this.finish(context, false);
      } catch (cleanupError) {
        throw new AggregateError([error, cleanupError], "Token cache access and cleanup failed.", { cause: cleanupError });
      }
      throw error;
    }
    transaction.settled = true;
    if (transaction.aborted) await this.finish(context, false);
  }

  async afterCacheAccess(context: TokenCacheContext): Promise<void> {
    const transaction = this.transactions.get(context);
    if (!transaction) return;
    if (!transaction.settled) return this.abortCacheAccess(context);
    return this.finish(context, context.cacheHasChanged && !transaction.aborted);
  }

  async abortCacheAccess(context: TokenCacheContext): Promise<void> {
    const transaction = this.transactions.get(context);
    if (!transaction) return;
    transaction.aborted = true;
    if (transaction.settled) await this.finish(context, false);
    // Do not await an uncancelable native before here. Its continuation owns
    // the eventual cleanup, and never persists an aborted transaction.
  }

  private async finish(context: TokenCacheContext, changed: boolean): Promise<void> {
    const transaction = this.transactions.get(context);
    if (!transaction) return;
    if (!transaction.completion) {
      transaction.completion = (async () => {
        try {
          await transaction.plugin.afterCacheAccess(new TokenCacheContext(context.tokenCache, changed));
        } finally {
          this.transactions.delete(context);
        }
      })();
    }
    return transaction.completion;
  }
}

/** Race an operation against the secure-storage timeout. The timer never keeps the process alive. */
async function withSecureStorageTimeout<T>(operation: Promise<T>, what: string): Promise<T> {
  const timeoutMs = getSecureStorageTimeoutMs();
  let timer: NodeJS.Timeout | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(secureStorageTimeoutError(what, timeoutMs)), timeoutMs);
        timer.unref?.();
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function defaultIsPidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (err) {
    // EPERM = exists but owned by another user — still alive.
    return (err as NodeJS.ErrnoException).code === "EPERM";
  }
}

/**
 * Remove a stale msal-node-extensions lockfile left behind by a killed process.
 *
 * CrossPlatformLock writes `<cachePath>.lockfile` containing its pid and only
 * removes it on clean shutdown — a killed/crashed server leaves it behind, and
 * every subsequent start then waits on a lock nobody holds. Observed live.
 * Only a lockfile whose pid is verifiably dead is removed; live or
 * unrecognizable holders are left alone.
 *
 * Returns true when a stale lockfile was removed. `isPidAlive` is injectable
 * for tests.
 */
export function clearStaleLockfile(
  cachePath: string,
  isPidAlive: (pid: number) => boolean = defaultIsPidAlive
): boolean {
  const lockPath = `${cachePath}.lockfile`;
  if (!existsSync(lockPath)) return false;

  let content: string;
  try {
    content = readFileSync(lockPath, "utf-8").trim();
  } catch {
    return false;
  }

  const pid = Number.parseInt(content, 10);
  if (!Number.isInteger(pid) || pid <= 0 || String(pid) !== content) {
    logger.warn({ lockPath, content: content.slice(0, 40) }, "Token-cache lockfile has unrecognized content — leaving it alone");
    return false;
  }

  if (isPidAlive(pid)) {
    logger.debug({ lockPath, pid }, "Token-cache lockfile held by a live process");
    return false;
  }

  try {
    unlinkSync(lockPath);
    logger.warn({ lockPath, pid }, "Removed stale token-cache lockfile left by a dead process");
    return true;
  } catch (err) {
    logger.warn({ lockPath, err: formatError(err) }, "Failed to remove stale token-cache lockfile");
    return false;
  }
}

/**
 * Bounds the FIRST cache read with the secure-storage timeout so a blocked
 * keychain (authorization prompt with no UI to show it) fails loudly instead
 * of silently hanging the MCP transport. After one successful read the
 * keychain is known-authorized and the hot path runs unwrapped; writes are
 * never timed out (they only happen after a successful read, and abandoning a
 * write could lose tokens).
 */
export class TimeoutCachePlugin implements AbortableCachePlugin {
  private firstReadSucceeded = false;

  constructor(private readonly inner: ICachePlugin) {}

  async beforeCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    if (this.firstReadSucceeded) {
      return this.inner.beforeCacheAccess(cacheContext);
    }
    try {
      await withSecureStorageTimeout(this.inner.beforeCacheAccess(cacheContext), "token cache read");
    } catch (error) {
      await this.abortCacheAccess(cacheContext);
      throw error;
    }
    this.firstReadSucceeded = true;
  }

  async afterCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    return this.inner.afterCacheAccess(cacheContext);
  }

  async abortCacheAccess(context: TokenCacheContext): Promise<void> {
    return abortCacheAccess(this.inner, context);
  }
}

/**
 * Skips redundant secure-storage reads.
 *
 * MSAL's persistence plugin reloads the OS store on EVERY token acquisition
 * to catch writes from other processes. On macOS each of those reads can be a
 * keychain authorization prompt (Homebrew's node is ad-hoc signed, so "Always
 * Allow" often doesn't stick, and every node upgrade changes the binary's
 * identity) — users reported one password prompt PER TOOL CALL.
 *
 * Fix: read the store once, then re-read only when a marker file says another
 * process actually wrote tokens. Every writer is one of our own processes
 * (the server, the sign_in tool, --login, --setup), and they all bump the
 * marker in afterCacheAccess — so an unchanged marker means the in-memory
 * MSAL cache is already current and the read can be skipped. Missing marker
 * (first run, older version) fails open: sync every time, exactly the old
 * behavior. PA_MCP_TOKEN_CACHE_SYNC=always restores the old behavior outright.
 */
/** The cross-process "tokens changed" signal lives next to the cache file. */
export function syncMarkerPath(cacheLocation: string): string {
  return `${cacheLocation}.sync`;
}

export class MarkerSyncedCachePlugin implements AbortableCachePlugin {
  private lastSeenMarker: string | null = null;
  private loadedOnce = false;
  private readonly delegatedContexts = new WeakSet<TokenCacheContext>();
  private readonly failedBeforeAccess = new WeakSet<TokenCacheContext>();

  constructor(
    private readonly inner: ICachePlugin,
    private readonly markerPath: string,
  ) {}

  private readMarker(): string | null {
    try {
      return existsSync(this.markerPath) ? readFileSync(this.markerPath, "utf-8") : null;
    } catch {
      return null;
    }
  }

  private bumpMarker(): void {
    try {
      const nonce = `${Date.now()}-${process.pid}-${Math.random().toString(36).slice(2)}`;
      writeFileSync(this.markerPath, nonce, { encoding: "utf-8", mode: 0o600 });
      this.lastSeenMarker = nonce;
    } catch (err) {
      logger.debug(
        { err: err instanceof Error ? err.message : String(err) },
        "Could not write token-cache sync marker — falling back to eager sync"
      );
      this.lastSeenMarker = null;
    }
  }

  async beforeCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    const eager = process.env["PA_MCP_TOKEN_CACHE_SYNC"]?.trim().toLowerCase() === "always";
    const marker = this.readMarker();
    if (
      !eager &&
      !cacheContext.cacheHasChanged &&
      this.loadedOnce &&
      marker !== null &&
      marker === this.lastSeenMarker
    ) {
      // Nothing external changed since our last load — the in-memory cache is
      // current; skip the secure-storage read (and its paired after hook).
      // Write-intent before hooks must still acquire the persistence lock.
      return;
    }
    this.delegatedContexts.add(cacheContext);
    try {
      await this.inner.beforeCacheAccess(cacheContext);
    } catch (error) {
      // MSAL calls after in finally even when before throws. Permit cleanup,
      // never a write of the stale snapshot after a failed load/lock attempt.
      this.failedBeforeAccess.add(cacheContext);
      throw error;
    }
    if (!this.delegatedContexts.has(cacheContext)) return;
    this.loadedOnce = true;
    this.lastSeenMarker = marker;
    if (marker === null) {
      // Seed the marker so this and sibling processes can start skipping.
      this.bumpMarker();
    }
  }

  async afterCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    if (!this.delegatedContexts.delete(cacheContext)) return;
    if (this.failedBeforeAccess.delete(cacheContext)) {
      return abortCacheAccess(this.inner, cacheContext);
    }
    await this.inner.afterCacheAccess(cacheContext);
    if (cacheContext.cacheHasChanged) {
      this.bumpMarker();
    }
  }

  async abortCacheAccess(context: TokenCacheContext): Promise<void> {
    this.failedBeforeAccess.delete(context);
    if (!this.delegatedContexts.delete(context)) return;
    return abortCacheAccess(this.inner, context);
  }
}

/**
 * Count the accounts in a serialized MSAL cache. Returns -1 when the answer
 * is unknowable (unparseable/absent data) so callers can fail open instead of
 * mistaking "can't tell" for "empty" — that confusion is how caches get lost.
 */
export function countAccountsInSerializedCache(data: string | null | undefined): number {
  if (data === null || data === undefined) return -1;
  const trimmed = data.trim();
  if (trimmed === "") return 0;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== "object") return -1;
    const accounts = (parsed as Record<string, unknown>)["Account"];
    if (accounts === undefined || accounts === null) return 0;
    if (typeof accounts !== "object") return -1;
    return Object.keys(accounts).length;
  } catch {
    return -1;
  }
}

let emptyWritePermits = 0;

/**
 * Permit the NEXT account-less cache write to reach persistent storage.
 *
 * The empty-write guard below refuses to overwrite a store that still holds
 * accounts with a cache that has none — that transition is how every process
 * sharing the store gets signed out at once, and it has happened in the field
 * ("token cache emptied mid-session"). A deliberate sign-out is the one
 * legitimate producer of that transition, so logout paths call this first.
 */
export function permitEmptyTokenCacheWrite(): void {
  emptyWritePermits += 1;
}

/** Test seam — reset outstanding permits. */
export function resetEmptyTokenCacheWritePermits(): void {
  emptyWritePermits = 0;
}

/**
 * Guards the shared token store against the two destructive transitions
 * observed in the field (several server processes — and two installed
 * generations — share one keychain item, so one bad writer signs out all):
 *
 * 1. READ-WIPE: the store comes back empty mid-session (a sibling process
 *    clobbered it, or secure storage transiently returned nothing) and the
 *    persistence plugin deserializes that emptiness OVER a healthy in-memory
 *    cache — the process instantly forgets a working sign-in. Guard: restore
 *    the in-memory snapshot and keep serving; the next legitimate token write
 *    re-persists the accounts (natural self-heal).
 *
 * 2. EMPTY-WRITE: a process with no accounts persists its empty cache over a
 *    store that still has accounts — signing out every other process. Guard:
 *    peek at the store before an account-less write and refuse it (loudly)
 *    unless a sign-out explicitly permitted it via permitEmptyTokenCacheWrite().
 *
 * Both guards fail open on "can't tell" (unparseable data, peek errors): the
 * guard must never become a new way for a working write to fail.
 */
export class GuardedCachePlugin implements AbortableCachePlugin {
  constructor(
    private readonly inner: ICachePlugin,
    /** Reads the CURRENT persisted store contents (best-effort, may throw). */
    private readonly peekStore?: () => Promise<string | null>,
  ) {}

  async beforeCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    const snapshot = cacheContext.tokenCache.serialize();
    const accountsBefore = countAccountsInSerializedCache(snapshot);

    await this.inner.beforeCacheAccess(cacheContext);

    if (accountsBefore > 0) {
      const accountsAfter = countAccountsInSerializedCache(cacheContext.tokenCache.serialize());
      if (accountsAfter === 0) {
        cacheContext.tokenCache.deserialize(snapshot);
        logger.warn(
          { accountsBefore },
          "Token store came back EMPTY while this process holds signed-in accounts — " +
            "keeping the in-memory sign-in instead of wiping it (another process may have " +
            "clobbered the shared store, or secure storage returned nothing transiently)."
        );
      }
    }
  }

  async afterCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    if (!cacheContext.cacheHasChanged) {
      return this.inner.afterCacheAccess(cacheContext);
    }

    const outgoingAccounts = countAccountsInSerializedCache(cacheContext.tokenCache.serialize());
    if (outgoingAccounts === 0) {
      if (emptyWritePermits > 0) {
        emptyWritePermits -= 1;
        return this.inner.afterCacheAccess(cacheContext);
      }
      if (this.peekStore) {
        let storeAccounts = -1;
        try {
          storeAccounts = countAccountsInSerializedCache(await this.peekStore());
        } catch (err) {
          logger.debug(
            { err: formatError(err) },
            "Could not peek token store before an empty write — allowing the write"
          );
        }
        if (storeAccounts > 0) {
          logger.warn(
            { storeAccounts },
            "REFUSED to overwrite the shared token store with an account-less cache — " +
              "the store still holds signed-in accounts other processes are using. " +
              "(A deliberate sign-out permits this via permitEmptyTokenCacheWrite.)"
          );
          // A write-intent before hook may already hold the persistence lock.
          // Complete that transaction without saving the refused empty cache.
          return abortCacheAccess(this.inner, cacheContext);
        }
      }
    }

    return this.inner.afterCacheAccess(cacheContext);
  }

  async abortCacheAccess(context: TokenCacheContext): Promise<void> {
    return abortCacheAccess(this.inner, context);
  }
}

type MsalNodeExtensionsModule = typeof import("@azure/msal-node-extensions");

let msalNodeExtensionsPromise: Promise<MsalNodeExtensionsModule> | null = null;

/**
 * Load msal-node-extensions lazily.
 *
 * On Linux, importing the package can fail immediately if libsecret is missing
 * because the native keytar module is loaded during module initialization. We
 * defer the import so the Linux file-cache fallback can still run.
 */
async function loadMsalNodeExtensions(): Promise<MsalNodeExtensionsModule> {
  if (!msalNodeExtensionsPromise) {
    msalNodeExtensionsPromise = import("@azure/msal-node-extensions").then((mod) => {
      const moduleWithOptionalDefault = mod as MsalNodeExtensionsModule & {
        default?: MsalNodeExtensionsModule;
      };
      const resolved = moduleWithOptionalDefault.default ?? moduleWithOptionalDefault;

      if (!resolved || !("PersistenceCreator" in resolved)) {
        throw new Error("msal-node-extensions did not expose the expected exports");
      }

      return resolved;
    });
  }

  return msalNodeExtensionsPromise;
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Native filesystem cache plugin.
 *
 * Reads/writes the serialized MSAL token cache directly to a file
 * with 0o600 permissions. Used as a fallback when msal-node-extensions
 * fails (e.g. KDE KWallet, headless Linux, WSL without keyring).
 */
export class NativeFileCachePlugin implements ICachePlugin {
  private readonly cachePath: string;

  constructor(cachePath: string) {
    this.cachePath = cachePath;
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- MSAL's ICachePlugin contract requires a Promise-returning method.
  async beforeCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    try {
      if (existsSync(this.cachePath)) {
        const data = readFileSync(this.cachePath, "utf-8");
        // Guard against empty/corrupt files
        if (data.length > 2) {
          cacheContext.tokenCache.deserialize(data);
        }
      }
    } catch (err) {
      logger.debug(
        { err: err instanceof Error ? err.message : String(err) },
        "Failed to read token cache file"
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- MSAL's ICachePlugin contract requires a Promise-returning method.
  async afterCacheAccess(cacheContext: TokenCacheContext): Promise<void> {
    if (cacheContext.cacheHasChanged) {
      try {
        const data = cacheContext.tokenCache.serialize();
        writeFileSync(this.cachePath, data, { encoding: "utf-8", mode: 0o600 });
        // Ensure permissions even if file already existed
        chmodSync(this.cachePath, 0o600);
      } catch (err) {
        logger.error(
          { err: err instanceof Error ? err.message : String(err) },
          "Failed to write token cache file"
        );
      }
    }
  }
}

/**
 * Create a persistent cache plugin for MSAL
 *
 * Uses platform-appropriate secure storage:
 * - Windows: DPAPI encryption (CurrentUser scope)
 * - macOS: Keychain
 * - Linux: libsecret if available, native fs fallback otherwise
 */
export async function createPersistentCachePlugin(
  cachePath?: string
): Promise<ICachePlugin> {
  const cacheLocation = cachePath ?? join(getConfigDir(), TOKEN_CACHE_FILE_NAME);

  logger.debug({ cacheLocation, platform }, "Creating persistent token cache");

  // A killed process leaves the cross-process lockfile behind; recover before
  // the persistence plugin tries (and fails forever) to acquire it.
  clearStaleLockfile(cacheLocation);

  // Windows and macOS: use msal-node-extensions (DPAPI / Keychain)
  if (platform === "win32" || platform === "darwin") {
    try {
      const {
        DataProtectionScope,
        PersistenceCreator,
        PersistenceCachePlugin,
      } = await loadMsalNodeExtensions();

      const persistence = await withSecureStorageTimeout(
        PersistenceCreator.createPersistence({
          cachePath: cacheLocation,
          dataProtectionScope: platform === "win32" ? DataProtectionScope.CurrentUser : undefined,
          serviceName: "powerautomate-mcp",
          accountName: "token-cache",
          usePlaintextFileOnLinux: false,
        }),
        "persistence creation"
      );

      logger.info({ cacheLocation }, "Persistent token cache initialized (platform secure storage)");
      return new GuardedCachePlugin(
        new MarkerSyncedCachePlugin(
          new TimeoutCachePlugin(new TransactionalCachePlugin(() => new PersistenceCachePlugin(persistence))),
          syncMarkerPath(cacheLocation),
        ),
        () => withSecureStorageTimeout(persistence.load(), "token store peek"),
      );
    } catch (err) {
      logger.error({ err, cacheLocation }, "Failed to create persistent cache");
      // npm 12 blocks dependency install scripts by default; a blocked keytar
      // build takes the whole msal-node-extensions import down with it (it
      // imports keytar statically on every platform). Name the real cause —
      // this failure otherwise masquerades as auth/consent problems.
      let hint = "";
      try {
        const { nativeStorageLoadError, isSystemDependencyFailure, manualRepairInstruction } =
          await import("../setup/native-repair.js");
        const loadError = nativeStorageLoadError();
        if (loadError !== null) {
          hint = isSystemDependencyFailure(loadError)
            ? ` Cause: a system library secure storage needs is missing. ${manualRepairInstruction(loadError)}`
            : " Cause: npm's install-script policy blocked a native component at install time. " +
              `Run 'powerautomate-mcp --doctor' from a terminal (it repairs this automatically). ` +
              manualRepairInstruction(loadError);
        }
      } catch {
        // diagnosis is best-effort
      }
      throw new Error(
        `Failed to initialize token cache at ${cacheLocation}: ${formatError(err)}.${hint}`,
        { cause: err }
      );
    }
  }

  // Linux: try libsecret first, fall back to native fs
  try {
    const {
      PersistenceCreator,
      PersistenceCachePlugin,
    } = await loadMsalNodeExtensions();

    const persistence = await withSecureStorageTimeout(
      PersistenceCreator.createPersistence({
        cachePath: cacheLocation,
        serviceName: "powerautomate-mcp",
        accountName: "token-cache",
        usePlaintextFileOnLinux: false,
      }),
      "persistence creation"
    );

    // Verify persistence actually works (catches KWallet/DBus failures
    // that only surface on first read/write, not on creation)
    await withSecureStorageTimeout(persistence.verifyPersistence(), "persistence verification");

    logger.info({ cacheLocation }, "Persistent token cache initialized (libsecret)");
    return new GuardedCachePlugin(
      new MarkerSyncedCachePlugin(
        new TimeoutCachePlugin(new TransactionalCachePlugin(() => new PersistenceCachePlugin(persistence))),
        syncMarkerPath(cacheLocation),
      ),
      () => withSecureStorageTimeout(persistence.load(), "token store peek"),
    );
  } catch (libsecretError) {
    logger.warn(
      { err: formatError(libsecretError) },
      "libsecret unavailable — using native file cache fallback (0o600 permissions)"
    );

    logger.info({ cacheLocation }, "Persistent token cache initialized (native file)");
    return new GuardedCachePlugin(
      new NativeFileCachePlugin(cacheLocation),
      // eslint-disable-next-line @typescript-eslint/require-await -- peek contract is async
      async () => (existsSync(cacheLocation) ? readFileSync(cacheLocation, "utf-8") : null),
    );
  }
}


/**
 * The "approve it once" repair for macOS keychain prompt storms.
 *
 * A keychain item is prompt-free only for the binary that CREATED it. Ours
 * gets created by whatever node ran first — after a Homebrew node upgrade
 * (new binary identity) every access prompts, and "Always Allow" often fails
 * to stick for ad-hoc-signed binaries. Rebuilding the item under the CURRENT
 * binary makes that binary the owner: one authorization now, then reads and
 * writes are silent until node changes again.
 *
 * `persistenceFactory` is injectable for tests; production callers pass
 * nothing.
 */
export async function rebuildSecureCacheItem(
  cachePath?: string,
  persistenceFactory?: (cacheLocation: string) => Promise<{
    load(): Promise<string | null>;
    save(contents: string): Promise<void>;
    delete(): Promise<boolean>;
  }>
): Promise<"rebuilt" | "empty" | "unsupported"> {
  if (platform !== "darwin" && !persistenceFactory) {
    return "unsupported";
  }
  const cacheLocation = cachePath ?? join(getConfigDir(), TOKEN_CACHE_FILE_NAME);
  clearStaleLockfile(cacheLocation);

  const createPersistence =
    persistenceFactory ??
    (async (location: string) => {
      const { DataProtectionScope, PersistenceCreator } = await loadMsalNodeExtensions();
      return PersistenceCreator.createPersistence({
        cachePath: location,
        dataProtectionScope: platform === "win32" ? DataProtectionScope.CurrentUser : undefined,
        serviceName: "powerautomate-mcp",
        accountName: "token-cache",
        usePlaintextFileOnLinux: false,
      });
    });

  const persistence = await withSecureStorageTimeout(
    Promise.resolve(createPersistence(cacheLocation)),
    "persistence creation"
  );
  // The one authorization this repair costs.
  const data = await withSecureStorageTimeout(persistence.load(), "token cache read");
  if (!data) {
    return "empty";
  }
  await persistence.delete();
  await persistence.save(data);
  return "rebuilt";
}

/**
 * Actually clear the persisted token cache — secure store included.
 *
 * The wizard and --login "clear cache" steps used to unlink only the cache
 * FILES, but on macOS/Windows the tokens live in the keychain/DPAPI store and
 * the file is just an mtime signal — so "cleared" cleared nothing, and stale
 * or wrong-app tokens survived a re-setup. This deletes the secure-store item
 * (permitting the resulting empty state past the guard) and reports what was
 * actually removed. Best-effort by design: a clear must never block sign-in.
 *
 * `persistenceFactory` is injectable for tests; production callers pass
 * nothing.
 */
export async function deleteSecureTokenCache(
  cachePath?: string,
  persistenceFactory?: (cacheLocation: string) => Promise<{ delete(): Promise<boolean> }>
): Promise<{ secureStoreCleared: boolean; detail?: string }> {
  const cacheLocation = cachePath ?? join(getConfigDir(), TOKEN_CACHE_FILE_NAME);

  if (platform !== "win32" && platform !== "darwin" && !persistenceFactory) {
    // Linux: the file IS the store (libsecret path also keeps the item keyed
    // by the same names, but deleting the files is what the callers already
    // do; a missing libsecret must not turn a clear into a hard failure).
    return { secureStoreCleared: false, detail: "no platform secure store to clear" };
  }

  try {
    clearStaleLockfile(cacheLocation);
    const createPersistence =
      persistenceFactory ??
      (async (location: string) => {
        const { DataProtectionScope, PersistenceCreator } = await loadMsalNodeExtensions();
        return PersistenceCreator.createPersistence({
          cachePath: location,
          dataProtectionScope: platform === "win32" ? DataProtectionScope.CurrentUser : undefined,
          serviceName: "powerautomate-mcp",
          accountName: "token-cache",
          usePlaintextFileOnLinux: false,
        });
      });
    const persistence = await withSecureStorageTimeout(
      Promise.resolve(createPersistence(cacheLocation)),
      "persistence creation"
    );
    permitEmptyTokenCacheWrite();
    await withSecureStorageTimeout(persistence.delete(), "token store delete");
    return { secureStoreCleared: true };
  } catch (err) {
    return { secureStoreCleared: false, detail: formatError(err) };
  }
}

/**
 * Get the default cache path
 */
export function getDefaultCachePath(): string {
  return join(getConfigDir(), TOKEN_CACHE_FILE_NAME);
}
